import { Injectable } from '@angular/core';
import {
  convertDesignIdToCommunicationId,
  convertDesignIdToTaskListGroups,
  convertEquipmentNumberToCommunicationId,
  convertEquipmentNumberToElementDesignId,
  convertEquipmentNumberToEquipmentRunningNumber,
  rethrowAsSapError,
  SapError,
} from 'src/app/lego-utils';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ITablePagination, TaskListHeader } from 'src/app/models/domain';
import { TaskListHeaderAdapter } from 'src/app/models/domain/adapters';
import { CollectionOfHeader, HeaderSetService } from 'src/generated-clients/tasklist';

interface ICountable extends CollectionOfHeader {
  __count: number; // TODO open upstream issue so __count is included in the generated client https://github.com/oasis-tcs/odata-openapi/issues
}

export interface IWithTotal<T> {
  data: T[];
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class SpecificationDataService {
  public constructor(
    private readonly taskListHeaderService: HeaderSetService,
    private readonly adapter: TaskListHeaderAdapter
  ) {}

  public getTechnicalSpecificationPageByEquipmentNumber(
    equipmentNumber: string,
    pagination: ITablePagination
  ) {
    const designId = convertEquipmentNumberToElementDesignId(equipmentNumber);
    const equipmentRunningNumber = convertEquipmentNumberToEquipmentRunningNumber(equipmentNumber);

    return this.getTaskListHeaderPageByElementIds(
      convertDesignIdToTaskListGroups(designId),
      equipmentRunningNumber,
      pagination
    );
  }

  public getTechnicalSpecificationPageByDesignId(designId: string, pagination: ITablePagination) {
    return this.getTaskListHeaderPageByElementIds2(
      convertDesignIdToTaskListGroups(designId),
      pagination
    );
  }

  public getControlSpecificationPageByDesignId(designId: string, pagination: ITablePagination) {
    const zeroPaddedCommunicationId = convertDesignIdToCommunicationId(designId).padStart(6, '0');

    const elementId = `CS${zeroPaddedCommunicationId}`;

    return this.getTaskListHeaderPageByElementIds2([elementId], pagination);
  }

  public getControlSpecificationsByEquipmentNumber(
    equipmentNumber: string,
    pagination: ITablePagination
  ) {
    const zeroPaddedCommunicationId = convertEquipmentNumberToCommunicationId(
      equipmentNumber
    ).padStart(6, '0');

    const equipmentRunningNumber = convertEquipmentNumberToEquipmentRunningNumber(equipmentNumber);

    const elementId = `CS${zeroPaddedCommunicationId}`;

    return this.getTaskListHeaderPageByElementIds([elementId], equipmentRunningNumber, pagination);
  }

  public getAllTechnicalSpecificationsByEquipmentNumber(equipmentNumber: string) {
    const designId = convertEquipmentNumberToElementDesignId(equipmentNumber);

    return this.getAllTechnicalSpecificationsByDesignId(designId);
  }

  public getAllTechnicalSpecificationsByDesignId(designId: string) {
    return this.getAllSpecifications(convertDesignIdToTaskListGroups(designId));
  }

  public getAllControlSpecificationsByEquipmentNumber(equipmentNumber: string) {
    const communicationId = convertEquipmentNumberToCommunicationId(equipmentNumber);

    const elementId = `CS${communicationId.padStart(6, '0')}`;

    return this.getAllSpecifications([elementId]);
  }

  private getAllSpecifications(elementIds: string[]): Observable<IWithTotal<TaskListHeader>> {
    const taskListTypePropertyName: keyof TaskListHeader = 'taskListType';
    const taskListGroupPropertyName: keyof TaskListHeader = 'taskListGroup';

    const typeFilter = `${taskListTypePropertyName} eq 'Q'`;

    const groupFilter = elementIds
      .map((id) => `(startswith(${taskListGroupPropertyName},'${id}'))`)
      .join(' or ');

    const filter = `${typeFilter} and (${groupFilter})`;

    return this.taskListHeaderService.headerSetGet(filter, undefined, undefined, 'allpages').pipe(
      rethrowAsSapError(),
      map((response) => {
        const d = response.d;
        const results = d?.results;

        if (d === undefined || results === undefined) {
          throw new SapError('Unknown error');
        }

        return {
          data: results.map((taskListHeader) => this.adapter.adaptToDomain(taskListHeader)),
          total: (d as ICountable).__count,
        };
      })
    );
  }

  private getTaskListHeaderPageByElementIds2(elementIds: string[], pagination: ITablePagination) {
    const taskListTypePropertyName: keyof TaskListHeader = 'taskListType';
    const taskListGroupPropertyName: keyof TaskListHeader = 'taskListGroup';

    const typeFilter = `${taskListTypePropertyName} eq 'Q'`;

    const groupFilter = elementIds
      .map((id) => `(startswith(${taskListGroupPropertyName},'${id}'))`)
      .join(' or ');

    const filter = `${typeFilter} and (${groupFilter})`;

    const { page, perPage } = pagination;
    const skip = page > 1 ? (page - 1) * perPage : undefined;

    return this.getPaginatedTaskListHeaders(filter, perPage, skip);
  }

  private getTaskListHeaderPageByElementIds(
    elementIds: string[],
    equipmentIdentifier: string,
    pagination: ITablePagination
  ) {
    const taskListTypePropertyName: keyof TaskListHeader = 'taskListType';
    const taskListGroupPropertyName: keyof TaskListHeader = 'taskListGroup';
    const taskListDescriptionPropertyName: keyof TaskListHeader = 'description';

    const typeFilter = `${taskListTypePropertyName} eq 'Q'`;

    const groupFilter = elementIds
      .map((id) => `(startswith(${taskListGroupPropertyName},'${id}'))`)
      .join(' or ');

    const equipmentFilter = `substringof('-${equipmentIdentifier}', ${taskListDescriptionPropertyName})`;

    const filter = `${typeFilter} and ((${groupFilter}) and ${equipmentFilter})`;

    const { page, perPage } = pagination;
    const skip = page > 1 ? (page - 1) * perPage : undefined;

    return this.getPaginatedTaskListHeaders(filter, perPage, skip);
  }

  private getPaginatedTaskListHeaders(filter: string, perPage: number, skip?: number) {
    return this.taskListHeaderService.headerSetGet(filter, perPage, skip, 'allpages').pipe(
      rethrowAsSapError(),
      map((taskListHeaderResponse) => {
        const d = taskListHeaderResponse.d;
        const results = d?.results;

        if (d === undefined || results === undefined) {
          throw new SapError('Unknown error');
        }

        return {
          data: results.map((taskListHeader) => this.adapter.adaptToDomain(taskListHeader)),
          total: (d as ICountable).__count,
        };
      })
    );
  }
}
